import React, { useState, useEffect } from 'react';

interface MasteryLearningPreviewProps {
  variant?: 'card' | 'featured';
}

const MasteryLearningPreview: React.FC<MasteryLearningPreviewProps> = ({ variant = 'card' }) => {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [mastery, setMastery] = useState(0);
  const [isLocked, setIsLocked] = useState(true);

  const levels = [
    { label: 'Foundation', threshold: 80, icon: '📐' },
    { label: 'Application', threshold: 80, icon: '🔧' },
    { label: 'Analysis', threshold: 80, icon: '🔍' },
    { label: 'Synthesis', threshold: 80, icon: '⭐' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setMastery(prev => {
        if (prev >= 85) {
          // Unlock next level
          setTimeout(() => {
            setIsLocked(false);
            setTimeout(() => {
              setCurrentLevel(l => (l + 1) % levels.length);
              setMastery(0);
              setIsLocked(true);
            }, 800);
          }, 200);
          return prev;
        }
        return prev + Math.floor(Math.random() * 15) + 5;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [currentLevel]);

  const height = variant === 'featured' ? 'h-48' : 'h-32';

  return (
    <div
      className={`relative ${height} w-full rounded-xl overflow-hidden`}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-1.5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span className="text-[10px] font-semibold" style={{ color: '#94a3b8' }}>
          Mastery Progression
        </span>
        <span className="text-[9px] font-medium" style={{ color: '#f59e0b' }}>
          80% to unlock
        </span>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Level indicators */}
        <div className="flex items-center justify-between mb-3">
          {levels.map((level, idx) => (
            <div key={level.label} className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  idx === currentLevel && !isLocked ? 'animate-bounce' : ''
                }`}
                style={{
                  background: idx < currentLevel
                    ? 'rgba(16, 185, 129, 0.2)'
                    : idx === currentLevel
                      ? 'rgba(245, 158, 11, 0.2)'
                      : 'rgba(255, 255, 255, 0.05)',
                  border: idx < currentLevel
                    ? '1px solid rgba(16, 185, 129, 0.4)'
                    : idx === currentLevel
                      ? '2px solid #f59e0b'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                {idx < currentLevel ? (
                  <span className="text-xs" style={{ color: '#10b981' }}>✓</span>
                ) : idx === currentLevel ? (
                  <span className="text-sm">{level.icon}</span>
                ) : (
                  <span className="text-xs" style={{ color: '#64748b' }}>🔒</span>
                )}
              </div>
              <span
                className="text-[8px] mt-1"
                style={{
                  color: idx <= currentLevel ? '#e2e8f0' : '#475569',
                  fontWeight: idx === currentLevel ? 600 : 400,
                }}
              >
                {level.label}
              </span>
            </div>
          ))}
        </div>

        {/* Mastery progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px]" style={{ color: '#94a3b8' }}>
              {levels[currentLevel].label} Mastery
            </span>
            <span
              className="text-[10px] font-bold"
              style={{ color: mastery >= 80 ? '#10b981' : '#f59e0b' }}
            >
              {Math.min(mastery, 100)}%
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(mastery, 100)}%`,
                background: mastery >= 80
                  ? 'linear-gradient(90deg, #10b981, #34d399)'
                  : 'linear-gradient(90deg, #f59e0b, #fbbf24)',
              }}
            />
            {/* Threshold marker */}
            <div
              className="absolute h-2 w-0.5"
              style={{
                left: 'calc(80% + 12px)',
                top: variant === 'featured' ? '140px' : '92px',
                background: 'rgba(255, 255, 255, 0.4)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Unlock notification */}
      {!isLocked && (
        <div
          className="absolute bottom-0 left-0 right-0 px-3 py-1.5 text-center animate-pulse"
          style={{ background: 'rgba(16, 185, 129, 0.2)' }}
        >
          <span className="text-[9px] font-semibold" style={{ color: '#10b981' }}>
            ✓ Mastery achieved! Next level unlocked
          </span>
        </div>
      )}
    </div>
  );
};

export default MasteryLearningPreview;
